/* eslint-disable no-case-declarations */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  getCurrentScreen,
  getNextScreenId,
  getScreenIdFromRoute,
} from "../util/screens";
import initialStore from "./initialStore";

// First screens in the form decision tree
const FORM_FIRST_SCREENS = ["1.5", "1.11"];

export const useAppStore = create(
  persist(
    (set, get) => ({
      // State
      token: null,
      user: {},
      form: {},
      formOptions: {},
      screens: [],
      initialState: {},

      // Actions
      //
      // UPDATE_FORM
      setFormAnswers: (state, payload) =>
        set({
          form: {
            ...state.form,
            dateStarted: payload.dateStarted,
            dateCompleted: payload.dateCompleted,
            questions: payload.answers,
          },
        }),
      // UPDATE_NAME (not previously implemented)
      setUpdateName: (state, firstName, lastName) =>
        set({
          user: {
            ...state.user,
            fname: firstName,
            lname: lastName,
          },
        }),
      // UPDATE_PORTFOLIO
      setPortfolio: (state, form) =>
        set({
          form: {
            ...state.form,
            formCalculation: form.formCalculation,
          },
        }),
      // UPDATE_LAST_SCREEN
      setLastScreen: (state) =>
        set({ form: { ...state.form, lastScreen: state.form.lastScreen + 1 } }),
      // SET_SELECTED_PORTFOLIO_ID
      // The portfolio the user has chosen, kept as its own field so the
      // accept-portfolio screen and form-submit logic don't have to fish a
      // numeric id out of form.questions by screen id. Picker screens call
      // this on every numeric selection; "last writer wins" naturally
      // mirrors the user's most recent choice as they move through the flow.
      setSelectedPortfolioId: (id) => {
        const numeric = Number(id);
        const normalized = !isNaN(numeric) && numeric > 0 ? numeric : null;
        set((state) => ({
          form: { ...state.form, selectedPortfolioId: normalized },
        }));
      },
      // UPDATE_ANSWER
      setAnswer: (questionId, answer) => {
        let foundValue = false;
        const questions = get().form.questions.map((q) => {
          if (q.questionId !== questionId) return q;
          else {
            foundValue = true;
            return {
              questionId: q.questionId,
              answerKey: answer,
            };
          }
        });
        if (!foundValue) {
          questions.push({
            questionId: questionId,
            answerKey: answer,
          });
        }
        return set((state) => ({
          form: {
            ...state.form,
            questions: questions,
          },
        }));
      },
      // SET_SCREEN
      // Reconciles store state with a URL change (deep links, refresh, browser
      // back/forward, programmatic navigate). The previous version always
      // pushed the leaving screen onto history, which corrupted the back
      // trail on every backward navigation: each Back/Continue cycle added
      // two entries, producing alternating-pattern garbage like
      // [..., A, B, A, B, A, B]. The fix is direction-aware:
      //   - landing on a screen already in history → backward jump → truncate
      //   - landing on a new screen → forward jump → push current
      setScreenFromRoute: async (location) => {
        const screenId = getScreenIdFromRoute(location.pathname);
        const currentState = get();

        // Don't update if we're already on this screen
        if (currentState.form?.currentScreen === screenId) {
          return screenId;
        }

        const previousHistory = currentState.form?.screenHistory || [];
        let screenHistoryUpdate;

        if (FORM_FIRST_SCREENS.includes(screenId)) {
          screenHistoryUpdate = [];
        } else {
          const idx = previousHistory.lastIndexOf(screenId);
          if (idx !== -1) {
            // Backward/lateral jump — truncate history at the target screen.
            screenHistoryUpdate = previousHistory.slice(0, idx);
          } else {
            // Forward jump to a new screen — push the current screen so we
            // can come back to it.
            screenHistoryUpdate = [
              ...previousHistory,
              currentState.form?.currentScreen,
            ].filter(Boolean);
          }
        }

        set((state) => ({
          form: {
            ...state.form,
            lastScreen: state.form?.currentScreen,
            currentScreen: screenId,
            screenHistory: screenHistoryUpdate,
          },
        }));

        return screenId;
      },
      // UPDATE_SCREEN - Add current screen to history before moving to next screen
      setScreen: async () => {
        const localState = get();
        const currentScreen = getCurrentScreen(localState);
        const nextScreenId = getNextScreenId(localState);

        // Only add current screen to history if it's not already the last item
        let screenHistory = [...(localState.form.screenHistory || [])];

        // Reset history if going to a first screen
        if (FORM_FIRST_SCREENS.includes(nextScreenId)) {
          screenHistory = [];
        }
        // Otherwise add current screen to history if it exists and isn't already the last item
        else if (currentScreen && currentScreen.id) {
          // Don't add duplicates at the end of history
          if (
            screenHistory.length === 0 ||
            screenHistory[screenHistory.length - 1] !== currentScreen.id
          ) {
            screenHistory.push(currentScreen.id);
          }
        }

        set((state) => ({
          form: {
            ...state.form,
            lastScreen: state.form.currentScreen,
            currentScreen: nextScreenId,
            screenHistory: screenHistory,
          },
        }));
        return nextScreenId;
      },

      // PREVIOUS_SCREEN - Pop the last screen from history.
      // Returns the previous screen id, or null if there's nowhere to go back to.
      // Returning null lets the Back button no-op rather than fall back to
      // `lastScreen`, which gets stuck pointing at the current screen and
      // produces a current<->previous toggle.
      setPreviousScreen: async () => {
        const localState = get();
        const currentScreenId = localState.form.currentScreen;
        let screenHistory = [...(localState.form.screenHistory || [])];

        if (screenHistory.length === 0) {
          return null;
        }

        let previousScreenId = screenHistory.pop();

        // Skip self-references that can sneak into history (e.g. duplicate pushes).
        while (previousScreenId === currentScreenId && screenHistory.length > 0) {
          previousScreenId = screenHistory.pop();
        }
        if (previousScreenId === currentScreenId) {
          return null;
        }

        // Self-heal corrupted (current, previous, current, previous, …) tails
        // left over from the pre-fix double-push bug. Without this, users with
        // accumulated alternations would have to click Back once per stale
        // entry; collapsing pairs lets the trail unwind in one action.
        while (
          screenHistory.length >= 2 &&
          screenHistory[screenHistory.length - 1] === currentScreenId &&
          screenHistory[screenHistory.length - 2] === previousScreenId
        ) {
          screenHistory.pop();
          screenHistory.pop();
        }

        // Special handling for screen 2.11 and 2.6 loop
        if (
          (currentScreenId === "2.11" && previousScreenId === "2.6") ||
          (currentScreenId === "2.6" && previousScreenId === "2.11")
        ) {
          previousScreenId = "2.5";
          screenHistory = [];
        }

        set((state) => ({
          form: {
            ...state.form,
            lastScreen: currentScreenId,
            currentScreen: previousScreenId,
            screenHistory: screenHistory,
          },
        }));

        return previousScreenId;
      },
      // CLEAR_SESSION
      // Full session wipe. Used on logout and when the refresh cookie is gone.
      clearSession: () => {
        try {
          useAppStore.persist.clearStorage();
        } catch (err) {
          console.error("Error clearing persisted store:", err);
        }
        set({
          user: initialStore.user,
          form: initialStore.form,
          formOptions: initialStore.formOptions,
          initialState: {},
          token: null,
        });
      },
      // RESET_FORM_FOR_NEW_USER
      // Wipes form state when a different user's session is bootstrapped
      // in a browser that still has the previous user's persisted data.
      // Keeps the freshly-issued access token in memory.
      resetFormForNewUser: () => {
        set((state) => ({
          form: initialStore.form,
          formOptions: initialStore.formOptions,
          initialState: {},
          token: state.token,
        }));
      },
      // UPDATE_USER_WITH_FORM
      setUserWithForm: (payload) => {
        console.log("setUserWithForm payload:", payload);
        const localState = get();
        // Log the payload to debug what's coming in

        const user = {
          ...localState.user,
          userId: payload.user.userId,
          fname: payload.user.firstName,
          lname: payload.user.lastName,
          emailAddress: payload.user.emailAddress,
          recordKeeper: payload.user.recordKeeperName,
          // Map both accountValue and accountAssets to ensure compatibility
          accountValue: payload.user.accountValue,
          accountAssets: payload.user.accountAssets,
          currentAge: payload.user.currentAge, // Add current age from backend
          portfolioId: payload.user.portfolioId,
          artesysPortfolioExists: !!payload.user.portfolioId,
          artesysPortfolioCode: payload.user.portfolioId,
          artesysPortfolioDescription: payload.user.portfolioId
            ? payload.formOptions.portfolios.find(
                (portfolio) => portfolio.id === payload.user.portfolioId,
              )?.name || "Default Portfolio"
            : "No Portfolio Selected",
          companyName: payload.user.companyName,
          quarterlyFeePercent: payload.user.quarterlyFeePercent,
          recommendedPortfolioId: payload.user.recommendedPortfolioId,
        };

        const form = {
          ...localState.form,
          dateStarted: payload.form.dateStarted,
          dateCompleted: payload.form.dateCompleted,
          lastScreen: payload.form.lastScreenCompleted ?? 0,
          questions: payload.form.questions ?? [],
        };
        const formOptions = {
          ...localState.formOptions,
          portfolios: payload.formOptions.portfolios,
          assets: payload.formOptions.assets,
          answerOptions: payload.formOptions.answerOptions,
          investmentStrategies: payload.formOptions.investmentStrategies,
        };
        return set({
          user,
          form,
          token: payload.token,
          formOptions,
        });
      },
    }),
    {
      name: "app-storage",
      // Token lives in memory only. The HttpOnly refresh cookie + bootstrap-on-load
      // restores the session on page refresh, so we no longer need to persist
      // the access token to localStorage where XSS could read it after the fact.
      partialize: (state) => {
        const persistableState = { ...state };
        delete persistableState.token;
        return persistableState;
      },
      merge: (persistedState, currentState) => {
        // If no persisted state, use initial state
        if (!persistedState || Object.keys(persistedState).length === 0) {
          return {
            ...currentState,
            ...initialStore,
            screens: initialStore.screens,
          };
        }

        // Get current screen from URL if available
        const pathname = window.location.pathname;
        const screenId = getScreenIdFromRoute(pathname);

        // Merge states with priority to URL-based screen.
        // If the URL screen is already inside the persisted back trail,
        // truncate so Back walks toward the start instead of forward into
        // stale entries from a later session.
        const persistedHistory = persistedState.form?.screenHistory || [];
        let mergedHistory;
        if (FORM_FIRST_SCREENS.includes(screenId)) {
          mergedHistory = [];
        } else if (screenId) {
          const idx = persistedHistory.lastIndexOf(screenId);
          mergedHistory =
            idx !== -1 ? persistedHistory.slice(0, idx) : persistedHistory;
        } else {
          mergedHistory = persistedHistory;
        }

        return {
          ...currentState,
          ...persistedState,
          screens: initialStore.screens,
          form: {
            ...persistedState.form,
            currentScreen: screenId || persistedState.form?.currentScreen,
            screenHistory: mergedHistory,
          },
        };
      },
    },
  ),
);
